// src/pages/produtos/produtos/ModalPrecosProduto.js
import React, { useEffect, useMemo, useState } from 'react';
import { fetchData, postData, deleteData } from '../../../services/Api';
import { useAlert } from '../../../contexts/geral/AlertContext';
import { formatNumeric, normalizeDecimalInputBr, parseCurrency } from '../../../utils/numberUtils.js';
import ModalPortal from '../../../components/modalForm/ModalPortal';

const diasSemana = [
  { label: 'D', nome: 'Domingo' },
  { label: 'S', nome: 'Segunda' },
  { label: 'T', nome: 'Terça' },
  { label: 'Q', nome: 'Quarta' },
  { label: 'Q', nome: 'Quinta' },
  { label: 'S', nome: 'Sexta' },
  { label: 'S', nome: 'Sábado' }
];

const horaStringParaInput = (hora) => {
  if (!hora || typeof hora !== 'string') return '';
  return hora.slice(0, 5);
};

const precoParaInput = (preco) => {
  if (preco === null || preco === undefined || preco === '') return '';
  return formatNumeric(preco, 2);
};

const parsePreco = (valor) => {
  if (valor === null || valor === undefined) return NaN;
  return parseCurrency(valor);
};

const ModalPrecosProduto = ({ show, onHide, produto }) => {
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registrosAtuais, setRegistrosAtuais] = useState([]);
  const [diasSelecionados, setDiasSelecionados] = useState([]);
  const [horarios, setHorarios] = useState({});
  const [erros, setErros] = useState({});

  const produtoId = produto?.id;

  const tituloProduto = useMemo(() => {
    if (!produto) return '';
    return `Produto ${produto.id} - ${produto.descricao || ''}`;
  }, [produto]);

  useEffect(() => {
    if (!show || !produtoId) return;

    const carregar = async () => {
      setLoading(true);
      setErros({});

      try {
        const data = await fetchData('/ProdutoPrecoDia', { produtoId });
        const lista = Array.isArray(data) ? data : [];
        const estrutura = {};
        const dias = [];

        lista.forEach((item) => {
          const dia = Number(item.diaSemana);
          dias.push(dia);

          estrutura[dia] = (item.periodos || []).map((p) => ({
            inicio: horaStringParaInput(p.horaInicio),
            fim: horaStringParaInput(p.horaFim),
            preco: precoParaInput(p.preco)
          }));

          if (!estrutura[dia] || estrutura[dia].length === 0) {
            estrutura[dia] = [{ inicio: '', fim: '', preco: '' }];
          }
        });

        setRegistrosAtuais(lista);
        setDiasSelecionados(dias.sort((a, b) => a - b));
        setHorarios(estrutura);
      } catch (error) {
        setRegistrosAtuais([]);
        setDiasSelecionados([]);
        setHorarios({});
        showAlert('Erro ao carregar preços do produto.', 'danger');
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [show, produtoId, showAlert]);

  if (!show) return null;

  const toggleDia = (dia) => {
    setErros((prev) => {
      const novo = { ...prev };
      delete novo[dia];
      return novo;
    });

    if (diasSelecionados.includes(dia)) {
      setDiasSelecionados((prev) => prev.filter((d) => d !== dia));

      setHorarios((prev) => {
        const novo = { ...prev };
        delete novo[dia];
        return novo;
      });
      return;
    }

    setDiasSelecionados((prev) => [...prev, dia].sort((a, b) => a - b));
    setHorarios((prev) => ({
      ...prev,
      [dia]: [{ inicio: '', fim: '', preco: '' }]
    }));
  };

  const alterarHorario = (dia, idx, campo, valor) => {
    setHorarios((prev) => {
      const lista = [...(prev[dia] || [])];
      lista[idx] = { ...lista[idx], [campo]: valor };
      return { ...prev, [dia]: lista };
    });

    setErros((prev) => {
      const novo = { ...prev };
      delete novo[`${dia}-${idx}-inicio`];
      delete novo[`${dia}-${idx}-fim`];
      delete novo[`${dia}-${idx}-preco`];
      delete novo[`${dia}-${idx}-intervalo`];
      return novo;
    });
  };

  const alterarPrecoHorario = (dia, idx, valor) => {
    alterarHorario(dia, idx, 'preco', normalizeDecimalInputBr(valor, 2).replace(/-/g, ''));
  };

  const formatarPrecoHorario = (dia, idx, valor) => {
    if (valor === null || valor === undefined || valor === '') {
      alterarHorario(dia, idx, 'preco', '');
      return;
    }

    alterarHorario(dia, idx, 'preco', formatNumeric(parseCurrency(valor), 2));
  };

  const adicionarPeriodo = (dia) => {
    setHorarios((prev) => ({
      ...prev,
      [dia]: [...(prev[dia] || []), { inicio: '', fim: '', preco: '' }]
    }));
  };

  const removerPeriodo = (dia, idx) => {
    setHorarios((prev) => {
      const lista = [...(prev[dia] || [])];
      lista.splice(idx, 1);
      return { ...prev, [dia]: lista };
    });
  };

  const handleSalvar = async () => {
    if (!produtoId) return;

    const novosErros = {};
    const payload = diasSelecionados.map((dia) => {
      const periodosValidos = [];

      (horarios[dia] || []).forEach((periodo, idx) => {
        const inicio = periodo?.inicio || '';
        const fim = periodo?.fim || '';
        const precoTexto = periodo?.preco || '';
        const preco = parsePreco(precoTexto);
        const temInicio = !!inicio;
        const temFim = !!fim;
        const temPreco = precoTexto !== '';

        if (!temInicio && !temFim && !temPreco) return;

        if (!temInicio) {
          novosErros[`${dia}-${idx}-inicio`] = 'Informe o início';
          return;
        }

        if (!temFim) {
          novosErros[`${dia}-${idx}-fim`] = 'Informe o fim';
          return;
        }

        if (!temPreco || Number.isNaN(preco) || preco < 0) {
          novosErros[`${dia}-${idx}-preco`] = 'Informe um preço válido';
          return;
        }

        if (inicio >= fim) {
          novosErros[`${dia}-${idx}-intervalo`] = 'Início deve ser menor que fim';
          return;
        }

        periodosValidos.push({
          horaInicio: `${inicio}:00`,
          horaFim: `${fim}:00`,
          preco
        });
      });

      return {
        produtoId,
        diaSemana: dia,
        periodos: periodosValidos
      };
    }).filter((item) => item.periodos.length > 0);

    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) return;

    setSaving(true);
    try {
      if (registrosAtuais.length > 0) {
        await Promise.all(
          registrosAtuais.map((item) => deleteData(`/ProdutoPrecoDia/${item.id}`))
        );
      }

      if (payload.length > 0) {
        await Promise.all(payload.map((item) => postData('/ProdutoPrecoDia', item)));
      }

      showAlert('Preços do produto salvos com sucesso.');
      onHide();
    } catch (error) {
      showAlert('Erro ao salvar preços do produto.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal show={show}>
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,.35)' }}>
      <div className="modal-dialog modal-lg" role="document" style={{ maxWidth: '820px', width: '95vw' }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Preços do Produto</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
          </div>

          <div className="modal-body">
            <div className="produto-horario-subtitle">{tituloProduto}</div>

            <div className="produto-horario-label">Dias da semana *</div>

            <div className="produto-dias-semana-container">
              {diasSemana.map((dia, index) => (
                <button
                  key={index}
                  type="button"
                  className={`produto-dia-badge ${diasSelecionados.includes(index) ? 'ativo' : ''}`}
                  onClick={() => toggleDia(index)}
                >
                  {dia.label}
                </button>
              ))}
            </div>

            <div className="produto-horario-observacao">
              O preço especial será aplicado apenas dentro dos horários cadastrados. Fora deles, vale o preço base do produto.
            </div>

            {loading ? (
              <div className="text-muted">Carregando preços...</div>
            ) : (
              <div className="produto-horarios-detalhe">
                {diasSelecionados.map((dia) => (
                  <div key={dia} className="produto-dia-horario-bloco">
                    <div className="produto-dia-nome">{diasSemana[dia].nome}:</div>

                    <div className="produto-periodos-container">
                      {(horarios[dia] || []).map((periodo, idx) => (
                        <div key={idx} className="produto-periodo-row">
                          <input
                            type="time"
                            value={periodo.inicio}
                            className={erros[`${dia}-${idx}-inicio`] || erros[`${dia}-${idx}-intervalo`] ? 'input-erro' : ''}
                            onChange={(e) => alterarHorario(dia, idx, 'inicio', e.target.value)}
                          />

                          <input
                            type="time"
                            value={periodo.fim}
                            className={erros[`${dia}-${idx}-fim`] || erros[`${dia}-${idx}-intervalo`] ? 'input-erro' : ''}
                            onChange={(e) => alterarHorario(dia, idx, 'fim', e.target.value)}
                          />

                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="Preço"
                            value={periodo.preco}
                            className={erros[`${dia}-${idx}-preco`] ? 'input-erro produto-preco-periodo-input' : 'produto-preco-periodo-input'}
                            onChange={(e) => alterarPrecoHorario(dia, idx, e.target.value)}
                            onBlur={(e) => formatarPrecoHorario(dia, idx, e.target.value)}
                          />

                          <button
                            type="button"
                            className="btn-remove-periodo"
                            onClick={() => removerPeriodo(dia, idx)}
                          >
                            -
                          </button>

                          <button
                            type="button"
                            className="btn-add-periodo"
                            onClick={() => adicionarPeriodo(dia)}
                          >
                            +
                          </button>

                          {(erros[`${dia}-${idx}-inicio`] || erros[`${dia}-${idx}-fim`] || erros[`${dia}-${idx}-preco`] || erros[`${dia}-${idx}-intervalo`]) && (
                            <div className="erro-texto">
                              {erros[`${dia}-${idx}-inicio`] || erros[`${dia}-${idx}-fim`] || erros[`${dia}-${idx}-preco`] || erros[`${dia}-${idx}-intervalo`]}
                            </div>
                          )}
                        </div>
                      ))}

                      {(horarios[dia] || []).length === 0 && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => adicionarPeriodo(dia)}
                        >
                          + Adicionar preço
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onHide} disabled={saving}>
              Fechar
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSalvar} disabled={saving || loading}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default ModalPrecosProduto;
